import React, { Component } from 'react';
import { Divider, Grid } from '@mui/material';

import { ColorByOption, UserSettings } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { InlineNotification } from '@paragon/notification-tools';
import { Dialog } from '@paragon/popup-state';

import { CameraSettingsColumn } from './columns/CameraSettingsColumn';
import { DimensionSettingsColumn } from './columns/DimensionSettingsColumn';
import { LayoutSettingsColumn } from './columns/LayoutSettingsColumn';
import { ViewOptionSettingsColumn } from './columns/ViewOptionSettingsColumn';
import { SettingsDialogContainer } from './SettingsDialogContainer';

interface Props {
  settings: UserSettings;
  onChange: (settings: UserSettings) => void;

  /**
   * Called when the dialog needs to be closed.
   */
  requestClose: () => void;

  /**
   * If `true`, layout-relevant settings will be shown. Defaults to `false`.
   */
  displayLayoutSettings?: boolean;

  /**
   * If set, the visible options for "Color By" will be limited to these choices.
   */
  enabledColorByOptions?: ColorByOption[];
}

interface State {
  error: string | null;

  initialSettings: UserSettings;
}

export class UserSettingsDialog extends Component<Props, State> {
  state: State = {
    error: null,

    initialSettings: this.props.settings,
  };

  componentDidMount() {
    // We want the application's active settings to represent the current user settings when the dialog is open.
    this.props.onChange(this.props.settings);
  }

  applySettingsChange =
    <K extends keyof UserSettings>(settingsKey: K) =>
    (newSettings: Partial<UserSettings[K]>) => {
      this.props.onChange({
        ...this.props.settings,
        [settingsKey]: { ...this.props.settings[settingsKey], ...newSettings },
      });
    };

  onCancel = () => {
    this.props.onChange(this.state.initialSettings);
    this.props.requestClose();
  };

  onSubmit = async () => {
    try {
      await designService.userSettings.postSetUserSettings(this.props.settings);
      this.props.requestClose();
    } catch (error) {
      console.error(error);
      this.setState({ error: 'Error saving user settings.  Please try again or contact Paragon Customer Support.' });
    }
  };

  renderErrorNotification() {
    if (this.state.error) {
      return <InlineNotification type="error">{this.state.error}</InlineNotification>;
    }
    return null;
  }

  renderContent() {
    if (this.props.settings == null) {
      return 'Loading settings...';
    }

    return (
      <SettingsDialogContainer>
        <Grid container>
          <Grid size={3}>
            <CameraSettingsColumn
              value={this.props.settings.cameraSettings}
              onChange={this.applySettingsChange('cameraSettings')}
            />

            {this.props.displayLayoutSettings && (
              <>
                <Divider sx={{ marginBottom: 2 }} />
                <LayoutSettingsColumn
                  value={this.props.settings.layoutSettings}
                  onChange={this.applySettingsChange('layoutSettings')}
                />
              </>
            )}
          </Grid>

          <Grid size={5}>
            <ViewOptionSettingsColumn
              value={this.props.settings.viewOptionSettings}
              enabledColorByOptions={this.props.enabledColorByOptions}
              onChange={this.applySettingsChange('viewOptionSettings')}
            />
          </Grid>

          <Grid size={4}>
            <DimensionSettingsColumn
              value={this.props.settings.dimensionSettings}
              onChange={this.applySettingsChange('dimensionSettings')}
            />
          </Grid>
        </Grid>
      </SettingsDialogContainer>
    );
  }

  render() {
    return (
      <Dialog
        title="User Settings"
        variant="design"
        onCancel={this.onCancel}
        onSubmit={this.onSubmit}
        disabled={this.props.settings == null}
      >
        {this.renderErrorNotification()}
        {this.renderContent()}
      </Dialog>
    );
  }
}
